import { createReducer } from '@reduxjs/toolkit';
import { IDemandOrderDto, IOrderDto, IReturnOrderDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    cancelDemandOrder,
    cancelOrder,
    cancelReturnOrder,
    clearOrderForOrderCancelation,
    importDemandOrderForCancelation,
    importOrderForCancelation,
    importReturnOrderForCancelation,
} from '../actions/orderActions';
import {
    clearBaseEntityState,
    compareModifiedDate,
    IBaseEntityState,
    IModifiedInfo,
    SaveEntityStatus,
    setBaseEntityCurrentJson,
} from './common';

export interface CancelOrderFormReducerState extends IBaseEntityState {
    order: IOrderDto | null;
    demandOrder: IDemandOrderDto | null;
    returnOrder: IReturnOrderDto | null;
}

function clearState(state: CancelOrderFormReducerState): void {
    state.order = null;
    state.demandOrder = null;
    state.returnOrder = null;

    clearBaseEntityState(state);
}

function getInitialState(): CancelOrderFormReducerState {
    const state: CancelOrderFormReducerState = {
        order: null,
        demandOrder: null,
        returnOrder: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

function calculateCurrentJson(state: CancelOrderFormReducerState, setAsBaseline: boolean = false): void {
    myAssert(() => state.order != null, 'Need order to calculate');

    const jsonBaseline = JSON.stringify(state.order);

    setBaseEntityCurrentJson(state, jsonBaseline, setAsBaseline);
}

function calculateBaseInfo(state: CancelOrderFormReducerState) {
    myAssert(() => state.order != null, 'Need order to calculate');

    if (
        (!state.order?.id || state.order?.id <= 0) &&
        (!state.demandOrder?.id || state.demandOrder?.id <= 0) &&
        (!state.returnOrder?.id || state.returnOrder?.id <= 0)
    ) {
        // no modified data should be set
    } else {
        // lets find the last modified part of order
        const modified: IModifiedInfo | undefined = compareModifiedDate(state.order, undefined);

        state.modified =
            (modified ?? state.order?.created)
                ? { modifiedAt: state.order?.created ?? new Date(), modifiedBy: state.order?.createdBy ?? '' }
                : undefined;
    }
}

export const cancelOrderFormReducer = createReducer<CancelOrderFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(importOrderForCancelation.fulfilled, (state: CancelOrderFormReducerState, action: any) => {
                clearState(state);

                const payload: IOrderDto = action.payload;

                myAssert(() => !!payload && !!payload.orderStatus, 'Required Data for Import');

                state.order = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(importDemandOrderForCancelation.fulfilled, (state: CancelOrderFormReducerState, action: any) => {
                clearState(state);

                const payload: IDemandOrderDto = action.payload;

                myAssert(() => !!payload, 'Required Data for Import');

                state.demandOrder = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(importReturnOrderForCancelation.fulfilled, (state: CancelOrderFormReducerState, action: any) => {
                clearState(state);

                const payload: IReturnOrderDto = action.payload;

                myAssert(() => !!payload, 'Required Data for Import');

                state.returnOrder = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(clearOrderForOrderCancelation, (state: CancelOrderFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(cancelOrder.pending, (state: CancelOrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(cancelOrder.fulfilled, (state: CancelOrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(cancelOrder.rejected, (state: CancelOrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            })
            .addCase(cancelDemandOrder.pending, (state: CancelOrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(cancelDemandOrder.fulfilled, (state: CancelOrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(cancelDemandOrder.rejected, (state: CancelOrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            })
            .addCase(cancelReturnOrder.pending, (state: CancelOrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(cancelReturnOrder.fulfilled, (state: CancelOrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(cancelReturnOrder.rejected, (state: CancelOrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            });
    },
);
